import { inject, Injectable, signal } from '@angular/core';
import { firstValueFrom } from 'rxjs';

import type { GoSkalerSetting } from '../models/access.model';
import { AccessApiService } from '../services/access-api.service';
import { isValidColor } from './color.utils';
import { AppliedTenantTheme, TenantTheme, THEME_COLOR_KEYS, ThemePalette } from './tenant-theme.types';

export const UX_PLATFORM_THEME_SETTING_KEY = 'GoSkaler.Web.UxPlatform';

const KEY_PREFIX = 'tenantTheme:';
const API_CACHE_PREFIX = `${KEY_PREFIX}api:`;
export const LAST_APPLIED_KEY = `${KEY_PREFIX}last`;

@Injectable({ providedIn: 'root' })
export class TenantThemeStorage {
  private readonly accessApi = inject(AccessApiService);

  readonly revision = signal(0);

  private readonly fetched = new Set<string>();

  constructor() {
    window.addEventListener('storage', (event) => {
      if (event.key?.startsWith(KEY_PREFIX) && event.key !== LAST_APPLIED_KEY) {
        this.revision.update((v) => v + 1);
      }
    });
  }

  get(tenantId: string): TenantTheme | null {
    this.fetchFromApi(tenantId);
    return this.peek(tenantId);
  }

  peek(tenantId: string): TenantTheme | null {
    return this.readLocal(API_CACHE_PREFIX + tenantId);
  }

  hasSaved(tenantId: string): boolean {
    return this.peek(tenantId) !== null;
  }

  /** Guarda el tema en la configuración del tenant (setting GoSkaler.Web.UxPlatform), el mismo que lee /api/me. */
  async set(tenantId: string, theme: TenantTheme): Promise<void> {
    const clean = sanitizeTheme(theme);
    if (!clean) return this.clear(tenantId);
    await this.save(tenantId, clean);
  }

  /** Quita el tema del tenant: vuelve a los colores por defecto de la plataforma. */
  async clear(tenantId: string): Promise<void> {
    await this.save(tenantId, null);
  }

  private async save(tenantId: string, theme: TenantTheme | null): Promise<void> {
    const value = theme ? JSON.stringify(theme) : null;
    await firstValueFrom(
      this.accessApi.upsertGoskalerSettings(tenantId, [{ key: UX_PLATFORM_THEME_SETTING_KEY, value, type: 'json' }])
    );
    try {
      if (value) localStorage.setItem(API_CACHE_PREFIX + tenantId, value);
      else localStorage.removeItem(API_CACHE_PREFIX + tenantId);
      localStorage.removeItem(KEY_PREFIX + tenantId);
    } catch {}
    this.revision.update((v) => v + 1);
  }

  setLastApplied(applied: AppliedTenantTheme | null): void {
    try {
      if (applied) localStorage.setItem(LAST_APPLIED_KEY, JSON.stringify(applied));
      else localStorage.removeItem(LAST_APPLIED_KEY);
    } catch {}
  }

  private fetchFromApi(tenantId: string): void {
    if (this.fetched.has(tenantId)) return;
    this.fetched.add(tenantId);

    this.accessApi.getMeForTenant(tenantId).subscribe({
      next: (me) => {
        const theme = parseTheme(findThemeSetting(me.goskalerSettings));
        try {
          if (theme) localStorage.setItem(API_CACHE_PREFIX + tenantId, JSON.stringify(theme));
          else localStorage.removeItem(API_CACHE_PREFIX + tenantId);
          localStorage.removeItem(KEY_PREFIX + tenantId);
        } catch {}
        this.revision.update((v) => v + 1);
      },
      error: () => this.fetched.delete(tenantId)
    });
  }

  private readLocal(key: string): TenantTheme | null {
    try {
      const raw = localStorage.getItem(key);
      return raw ? sanitizeTheme(JSON.parse(raw)) : null;
    } catch {
      return null;
    }
  }
}

function findThemeSetting(settings: GoSkalerSetting[] | undefined): string | undefined {
  return settings?.find((setting) => setting.key === UX_PLATFORM_THEME_SETTING_KEY)?.value;
}

function parseTheme(raw: string | undefined): TenantTheme | null {
  if (!raw) return null;
  try {
    return sanitizeTheme(JSON.parse(raw));
  } catch {
    return null;
  }
}

function sanitizeTheme(value: unknown): TenantTheme | null {
  if (!value || typeof value !== 'object') return null;
  const input = value as Record<string, unknown>;

  const light = sanitizePalette(input['light']);
  const dark = sanitizePalette(input['dark']);
  const radius = input['radius'];
  const mode = input['mode'];

  const theme: TenantTheme = { light };
  if (Object.keys(dark).length) theme.dark = dark;
  if (typeof radius === 'number' && radius >= 0 && radius <= 2) theme.radius = radius;
  if (mode === 'light' || mode === 'dark') theme.mode = mode;

  return Object.keys(light).length || theme.dark || theme.radius !== undefined || theme.mode ? theme : null;
}

function sanitizePalette(value: unknown): ThemePalette {
  const palette: ThemePalette = {};
  if (!value || typeof value !== 'object') return palette;
  for (const key of THEME_COLOR_KEYS) {
    const color = (value as Record<string, unknown>)[key];
    if (isValidColor(color)) palette[key] = color.trim();
  }
  return palette;
}
